import { chakra, Divider, Flex, useToast } from "@chakra-ui/react";
import { useFieldArray, useForm } from "react-hook-form";
import {
  supplierPhysicalRiskQuestionnaireKeys,
  useFinalizeSupplierPhysicalRiskQuestionnaire,
  useUpdateLocation
} from "../../../../hooks/query/supplierPhysicalRiskQuestionnaire";
import { prepareCategoryDefaultValues, showErrorToast, showSuccessToast } from "../../../../utils";
import { useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import * as React from "react";
import {
  FormTextArea
} from "../../../../components/Forms";
import { RiskAssessment } from "../RiskAssessment";
import { useEffect } from "react";
import { hideAppSpinner, showAppSpinner } from "../../../../context/Store/slices/appSpinnerSlice";
import { useDispatch } from "react-redux";
import {
  Answer,
  InsuranceCoveragesFieldArray,
  InsuranceCoveragesPreview,
  NaturalDisastersFieldArray,
  NaturalDisastersPreview
} from "./components";

const NATURAL_DISASTERS_FORM_KEY = "naturalDisasters";
const INSURANCE_FORM_KEY = "insuranceCoverages";

function PhysicalRiskAssessmentForm({
                                      physicalRiskId,
                                      currentElement,
                                      tabKey,
                                      handleLocationChange,
                                      finalize,
                                      isPreview,
                                      returnToListRequested,
                                      returnToList
                                    }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "supplierPhysicalRiskQuestionnaire"
  });

  const formId = `formStep${tabKey}`;
  const locationId = tabKey;
  const queryClient = useQueryClient();
  const toast = useToast();
  const currentLocation = currentElement;
  const dispatch = useDispatch();

  const locationDefaultValues = {
    [NATURAL_DISASTERS_FORM_KEY]: currentLocation.naturalDisasters ? currentLocation.naturalDisasters.map(el => {
      return {
        ...el,
        date: el.date ? new Date(el.date) : ""
      };
    }) : [
      {
        type: "",
        date: "",
        description: ""
      }
    ],
    [INSURANCE_FORM_KEY]: currentLocation.insuranceCoverages ?
      prepareCategoryDefaultValues(currentLocation.insuranceCoverages, ["coverage"]) : [
        {
          insuranceType: "",
          coverage: "",
          insuranceCompany: ""
        }
      ],
    affectedItems: currentLocation.affectedItems,
    impact: currentLocation.impact,
    profitabilityAffect: currentLocation.profitabilityAffect,
    initiatives: currentLocation.initiatives,
    affectedOtherBusinesses: currentLocation.affectedOtherBusinesses,
    municipalityInitiatives: currentLocation.municipalityInitiatives
  };

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isDirty }
  } = useForm({
    defaultValues: locationDefaultValues
  });

  const { fields: disastersFields, append: appendDisaster, remove: removeDisaster } = useFieldArray({
    control,
    name: NATURAL_DISASTERS_FORM_KEY
  });

  const { fields: insuranceFields, append: appendInsurance, remove: removeInsurance } = useFieldArray({
    control,
    name: INSURANCE_FORM_KEY
  });

  const queryConfig = {
    onSuccess: () => {
      if (finalize) {
        finalizeQuestionnaire(physicalRiskId);
      } else {
        showSuccessToast(toast, t("locationSaved", { locationName: currentLocation.location }));
        if (returnToListRequested === true) {
          returnToList();
        }
        queryClient.invalidateQueries(supplierPhysicalRiskQuestionnaireKeys.supplierPhysicalRiskQuestionnaireById());
        if (!isLoadingOnUpdate) {
          handleLocationChange();
        }
      }
    },
    onError: err => showErrorToast(toast, err.message),
    onSettled: () => dispatch(hideAppSpinner())
  };

  const { mutate: updateLocation, isLoading: isLoadingOnUpdate } = useUpdateLocation(queryConfig);
  const {
    mutate: finalizeQuestionnaire,
    isLoading: isLoadingOnFinalize
  } = useFinalizeSupplierPhysicalRiskQuestionnaire({
    onSuccess: () => {
      showSuccessToast(toast, t("questionnaireFinalised"));
      returnToList();
    },
    onError: err => showErrorToast(toast, err.message),
    onSettled: () => {
      dispatch(hideAppSpinner());
    }
  });

  useEffect(() => {
    if (isLoadingOnUpdate || isLoadingOnFinalize) {
      dispatch(showAppSpinner());
    }
  }, [dispatch, isLoadingOnUpdate, isLoadingOnFinalize]);

  const onSubmit = ((data) => {
    if (isDirty && locationId) {
      updateLocation({ physicalRiskId, locationId, data });
    } else {
      finalize ? finalizeQuestionnaire(physicalRiskId) :
        returnToListRequested ? returnToList() : handleLocationChange();
    }
  });

  return <chakra.form
    id={formId}
    onSubmit={handleSubmit(onSubmit)}
    flexGrow="1"
    overflowY="auto"
    overflowX="visible"
    paddingBlockEnd="4"
    paddingInline="2"
    marginInline="-2"
    height={isPreview ? "calc(100% - var(--chakra-space-10))" : "100%"}
  >
    <Flex direction="column" gap={5}>
      <RiskAssessment
        index="1"
        body={isPreview ? currentLocation?.naturalDisasters && currentLocation?.naturalDisasters?.length > 0 ?
            <NaturalDisastersPreview
              items={currentLocation?.naturalDisasters}
            /> : <Answer answerText={""} /> :
          <NaturalDisastersFieldArray
            fields={disastersFields}
            register={register}
            errors={errors}
            control={control}
            formKey={NATURAL_DISASTERS_FORM_KEY}
            appendItem={appendDisaster}
            removeItem={removeDisaster}
          />
        }
      />
      <Divider />
      <RiskAssessment
        index="2"
        body={isPreview ?
          <Answer answerText={currentLocation.affectedItems} /> :
          <FormTextArea
            label={t("inputs.affectedItems")}
            placeholder={t("inputs.affectedItemsPlaceholder")}
            errors={errors}
            {...register("affectedItems")}
          />}
      />
      <RiskAssessment
        index="3"
        body={isPreview ?
          <Answer answerText={currentLocation.impact} /> :
          <FormTextArea
            label={t("inputs.impact")}
            placeholder={t("inputs.impactPlaceholder")}
            errors={errors}
            {...register("impact")}
          />}
      />
      <RiskAssessment
        index="4"
        body={isPreview ? <Answer answerText={currentLocation.profitabilityAffect} /> :
          <FormTextArea
            label={t("inputs.profitability")}
            placeholder={t("inputs.profitabilityPlaceholder")}
            errors={errors}
            {...register("profitabilityAffect")}
          />}
      />
      <RiskAssessment
        index="5"
        body={isPreview ? <Answer answerText={currentLocation.initiatives} /> :
          <FormTextArea
            label={t("inputs.initiatives")}
            placeholder={t("inputs.initiativesPlaceholder")}
            errors={errors}
            {...register("initiatives")}
          />}
      />
      <RiskAssessment
        index="6"
        body={isPreview ? <Answer answerText={currentLocation.affectedOtherBusinesses} /> :
          <FormTextArea
            label={t("inputs.otherBusinesses")}
            placeholder={t("inputs.otherBusinessesPlaceholder")}
            errors={errors}
            {...register("affectedOtherBusinesses")}
          />}
      />
      <RiskAssessment
        index="7"
        body={isPreview ? currentLocation?.insuranceCoverages && currentLocation?.insuranceCoverages?.length > 0 ?
            <InsuranceCoveragesPreview
              items={currentLocation?.insuranceCoverages}
            /> : <Answer answerText={""} /> :
          <InsuranceCoveragesFieldArray
            fields={insuranceFields}
            register={register}
            errors={errors}
            formKey={INSURANCE_FORM_KEY}
            appendItem={appendInsurance}
            removeItem={removeInsurance}
          />
        }
      />
      <Divider />
      <RiskAssessment
        index="8"
        body={isPreview ? <Answer answerText={currentLocation.municipalityInitiatives} /> :
          <FormTextArea
            label={t("inputs.initiatives")}
            placeholder={t("inputs.initiativesPlaceholder")}
            errors={errors}
            {...register("municipalityInitiatives")}
          />}
      />
    </Flex>
  </chakra.form>;
}

export default PhysicalRiskAssessmentForm;